/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  Grid,
} from 'antd';
import {
  QuestionCircleOutlined,
  IdcardOutlined,
  // PrinterOutlined,
} from '@ant-design/icons';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import { isMutable } from 'utils/estadosGlobales';
import makeMessager from 'utils/complexFormsMessages';
import NumericInput from 'components/NumericInput';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import {
  getLineasVehiculares,
  getMarcasDeVehiculos,
  getTiposDeVehiculos,
  getColoresVehiculos,
} from 'api/recaudacion';
import {
  deleteUnidadPoliciaca,
  getUnidadPoliciaca,
  getUnidadesPoliciacas,
  patchUnidadPoliciaca,
  postUnidadPoliciaca,
} from 'api/catalogos/unidadPoliciaca';
import { capitalize } from 'utils/normalizers';
import Moment from 'moment';
import TextArea from 'antd/lib/input/TextArea';

const { TabPane } = Tabs;
const { useBreakpoint } = Grid;
const baseURI = 'recaudacion/vehiculos/';

const UnidadPoliciaca = ({ permission }) => {
  const [form] = Form.useForm();
  const [formFactura] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [formDocumentos] = Form.useForm();
  const marca = Form.useWatch('marca', formInfo);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [mutable, setMutable] = useState(true);

  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [tipoDeVehiculo, setTipoDeVehiculo] = useState([]);
  const [colorVehiculo, setColorVehiculo] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [marcasTemp, setMarcasTemp] = useState({ results: [] });
  const [lineaTemp, setLineaTemp] = useState({ results: [] });
  const marcas = useRef({ results: [] });
  const linea = useRef({ results: [] });

  const basicKeys = ['general'];
  const complexForms = [];

  const screen = useBreakpoint();

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setSelectedRowKeys([]);
    setCurrentPage(page);
    const _data = await getUnidadesPoliciacas({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    formInfo.resetFields();
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
    setMutable(true);
    marcas.current = { results: [] };
    linea.current = { results: [] };
    setMarcasTemp({ results: [] });
    setLineaTemp({ results: [] });
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [
        _tipos,
        _coloresVehiculos,
        _data,
      ] = await Promise.all([
        getTiposDeVehiculos(),
        getColoresVehiculos(),
        getUnidadesPoliciacas({ page: 1 }),
      ]);
      if (mounted) {
        setTipoDeVehiculo(_tipos);
        setColorVehiculo(_coloresVehiculos);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onClickEdit = async (response) => {
    form.resetFields();
    formInfo.resetFields();
    const key = response?.id || selectedRowKeys[0];
    const match = await getUnidadPoliciaca(key);
    setMutable(isMutable(match.estados_globales));
    if (match) {
      if (match.linea) {
        const _linea = await getLineasVehiculares({ id: match.linea });
        setLineaTemp(_linea);
        const _marca = await getMarcasDeVehiculos({ id: _linea.results[0].marca });
        setMarcasTemp(_marca);
        match.marca = _linea.results[0].marca;
      }
      setTimeout(() => {
        form.setFieldsValue({ ...match });
        formInfo.setFieldsValue({ ...match });
        formDocumentos.setFieldsValue(match);
        formFactura.setFieldsValue(match);
        setVisible(true);
      });
    }
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formInfo.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      if (key) {
        values.fecha_del_ultimo_movimiento = new Moment();
      }
      const response = await (key
        ? patchUnidadPoliciaca(key, values) : postUnidadPoliciaca(values));
      if (response) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([response.id]);
            onClickEdit(response);
          } else {
            onClickEdit(response);
          }
        } else {
          onCancel();
        }
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form, formInfo], setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteUnidadPoliciaca(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setMutable(isMutable(record));
    setSelectedRowKeys([record.id]);
    setTimeout(() => {
      form.setFieldsValue(record);
      formInfo.setFieldsValue(record);
      formDocumentos.setFieldsValue(record);
    });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'No. de Placa',
      dataIndex: 'numero_de_placa',
      key: 'numero_de_placa',
      width: 150,
    },
    {
      titleText: 'Tipo de Vehìculo',
      dataIndex: 'tipo_de_vehiculo',
      key: 'tipo_de_vehiculo',
      width: 200,
      catalog: {
        cat: tipoDeVehiculo,
        labelProp: 'nombre',
        use: true,
      },
    },
  ];

  const rules = {
    required: [{ required: true }],
    archivos: [
      {
        validator: async () => {
          const { factura_del_vehiculo, pedimento_del_vehiculo } = formInfo.getFieldsValue();
          if (factura_del_vehiculo?.fileList?.length
            && pedimento_del_vehiculo?.fileList?.length) {
            throw new Error('Solo es necesario la factura o el pedimento');
          }
        },
      },
    ],
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys,
    complexForms,
    currentTabKey,
    setCurrentTabKey,
    showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  const allowSearch = screen.md || screen.lg || screen.xl || screen.xxl;

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible && (
          <Table
            cols={columns}
            data={data?.results}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            rowKey="id"
            baseURI={baseURI}
            allowImport
            mobileColIndex={0}
            allowColumnSelector={false}
            allowSearch={allowSearch}
            permission={permission}
            pagination={false}
            paginationCallback={fetchData}
            count={data.count}
            currentPage={currentPage}
          />
        )}
        {visible && (
          <Col span={24}>
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Unidad`}
              extra={(
                <FormSubmitControls
                  onFinish={getOnFinish()}
                  onCancel={onCancel}
                  mutable={mutable}
                  allowSaveAndContinue
                  onCancelText="Cancelar"
                  permission={permission}
                />
              )}
              bordered={false}
            >
              <Tabs
                onChange={mutable ? onChangeTabKey : setCurrentTabKey}
                activeKey={currentTabKey}
                type="card"
              >
                <TabPane
                  tab={(
                    <span>
                      <IdcardOutlined />
                      Información General
                    </span>
                  )}
                  key="general"
                  forceRender
                >
                  <Form
                    name="info"
                    layout="vertical"
                    form={formInfo}
                    onFinish={() => onFinish()}
                    scrollToFirstError
                    initialValues={{
                      es_propietario_ciudadano: false,
                      exento_de_pago_de_tenencia: true,
                    }}
                  >
                    <Row gutter={10}>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="clave"
                          label="clave"
                          rules={rules.required}
                          hasFeedback
                          required
                        >
                          <Input allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="numero_de_unidad"
                          label="Número de unidad"
                          hasFeedback
                          required
                        >
                          <Input allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="descripcion"
                          label="Descripción"
                          hasFeedback
                          required
                        >
                          <Input allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="numero_de_placa"
                          label="Número de placa"
                          hasFeedback
                          required
                        >
                          <Input allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="numero_de_serie"
                          label="Número de serie"
                          hasFeedback
                          required
                        >
                          <Input allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          name="año_del_modelo"
                          label="Modelo"
                          tooltip={{
                            title: 'Año del vehículo',
                            icon: <QuestionCircleOutlined />,
                          }}
                          disabled={!mutable}
                          required
                        />
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="marca"
                          label="Marca"
                          hasFeedback
                          rules={rules.required}
                        >
                          <Select
                            dataSource={marcasTemp.results?.length ? marcasTemp : marcas.current}
                            usingExternalDataSource={Boolean(marcasTemp.results?.length)}
                            labelProp="nombre"
                            onClear={() => {
                              formInfo.resetFields(['linea', 'version_vehicular']);
                              setMarcasTemp({ results: [] });
                              setLineaTemp({ results: [] });
                            }}
                            onChange={() => {
                              formInfo.resetFields(['linea', 'version_vehicular']);
                              setMarcasTemp({ results: [] });
                              setLineaTemp({ results: [] });
                            }}
                            pagination={{
                              getter: getMarcasDeVehiculos,
                              setter: (dataSource) => {
                                marcas.current = dataSource;
                              },
                              config: { auto: true, qName: 'nombre' },
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="linea"
                          label="Línea"
                          hasFeedback
                          rules={rules.required}
                        >
                          <Select
                            dataSource={lineaTemp.results?.length ? lineaTemp : linea.current}
                            usingExternalDataSource={Boolean(lineaTemp.results?.length)}
                            labelProp="nombre"
                            disabled={!marca}
                            pagination={{
                              getter: getLineasVehiculares,
                              setter: (dataSource) => {
                                linea.current = dataSource;
                              },
                              params: { marca },
                              config: { auto: true, qName: 'nombre' },
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="tipo_de_vehiculo"
                          label="Tipo de Vehículo"
                          hasFeedback
                          rules={rules.required}
                        >
                          <Select
                            dataSource={tipoDeVehiculo}
                            labelProp="nombre"
                            disabled={!mutable}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="color_secundario"
                          label="Color Secundario"
                          hasFeedback
                          normalize={capitalize}
                        >
                          <Input
                            allowClear
                            disabled={!mutable}

                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="color_del_vehiculo"
                          label="Color del Vehículo"
                          hasFeedback
                          rules={rules.required}
                        >
                          <Select
                            dataSource={colorVehiculo}
                            disabled={!mutable}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={16}>
                        <Form.Item
                          name="observaciones"
                          rules={rules.required}
                          label="Observaciones"
                          hasFeedback
                        >
                          <TextArea autoSize={{ minRows: 3, maxRows: 3 }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item hidden>
                      <Button htmlType="submit" />
                    </Form.Item>
                  </Form>
                </TabPane>
              </Tabs>
            </Card>
          </Col>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="la unidad Seleccionada"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default UnidadPoliciaca;
